import React, { useState, useEffect } from 'react';
import { userAPI, workspaceAPI, authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiUsers, FiPlus, FiX, FiCheck, FiXCircle, FiEdit2, FiTrash2, FiSearch, FiMail, FiClock, FiShield, FiCreditCard } from 'react-icons/fi';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free', color: '#6b7280' },
  { value: 'standard', label: 'Standard', color: '#3b82f6' },
  { value: 'professional', label: 'Professional', color: '#8b5cf6' },
  { value: 'enterprise', label: 'Enterprise', color: '#059669' },
];

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const PAGE_SIZE = 25;
  const EMPTY_FILTERS = { role: '', status: '', workspace: '', lastLogin: '', sort: 'newest' };
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [workspaces, setWorkspaces] = useState([]);
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const activeFilterCount = ['role', 'status', 'workspace', 'lastLogin'].filter(k => filters[k]).length;
  const setFilter = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); };

  // Workspace filter options (super admins only; admins only ever see their own workspace)
  useEffect(() => {
    if (isSuperAdmin) workspaceAPI.getAll().then(r => setWorkspaces(r.data.workspaces || [])).catch(() => {});
  }, [isSuperAdmin]);
  const [assigningPlan, setAssigningPlan] = useState(null); // userId being assigned
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '', role: 'viewer', department: '', regionLevel: '', phone: ''
  });

  // Search runs on the server across all users; debounce so typing doesn't fire a request per key
  useEffect(() => {
    const t = setTimeout(() => load(), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [page, search, filters]);

  const load = async () => {
    try {
      const [all, pend, st] = await Promise.all([
        userAPI.getAll({
          page, limit: PAGE_SIZE,
          ...(search.trim() ? { search: search.trim() } : {}),
          ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
        }),
        userAPI.getPending().catch(() => ({ data: [] })), userAPI.getStats().catch(() => ({ data: {} }))
      ]);
      setUsers(all.data.users || []);
      setPages(all.data.pages || 1);
      setTotalUsers(all.data.total || 0);
      setPending(pend.data.users || pend.data || []);
      setStats(st.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        await userAPI.update(editUser._id, form);
        toast.success('User updated');
      } else {
        await userAPI.create(form);
        toast.success('User created');
      }
      setShowModal(false);
      setEditUser(null);
      setForm({ firstName: '', lastName: '', email: '', password: '', role: 'viewer', department: '', regionLevel: '', phone: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleApprove = async (id) => {
    try { await userAPI.approve(id); toast.success('User approved'); load(); } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleReject = async (id) => {
    try { await userAPI.reject(id); toast.success('User rejected'); load(); } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleResendCode = async (u) => {
    try {
      const { data } = await authAPI.resendVerification(u.email);
      toast.success(data.message || `New code sent to ${u.email}`);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to resend code'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    if (id === currentUser?.id || id === currentUser?._id) { toast.error("You can't delete your own account"); return; }
    try { await userAPI.delete(id); toast.success('User deleted'); load(); } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleAssignPlan = async (userId, planName) => {
    setAssigningPlan(userId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/stripe/admin-assign-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, planName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      toast.success(data.message);
      load();
    } catch (err) { toast.error(err.message || 'Failed to assign plan'); }
    setAssigningPlan(null);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ firstName: user.firstName, lastName: user.lastName, email: user.email, password: '', role: user.role, department: user.department || '', regionLevel: user.regionLevel || '', phone: user.phone || '' });
    setShowModal(true);
  };

  const roleColors = { super_admin: '#dc2626', admin: '#7c3aed', analyst: '#2563eb', field_worker: '#059669', viewer: '#6b7280' };
  const allRoleLabels = { super_admin: 'Super Admin', admin: 'Admin', analyst: 'Analyst', field_worker: 'Field Worker', viewer: 'Viewer' };
  // Only super_admin can assign super_admin role
  const roleLabels = currentUser?.role === 'super_admin' ? allRoleLabels : { admin: 'Admin', analyst: 'Analyst', field_worker: 'Field Worker', viewer: 'Viewer' };

  const filtered = users; // already filtered and paginated by the server

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>User Management</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Manage users, roles, and approval requests</p>
        </div>
        <button onClick={() => { setEditUser(null); setForm({ firstName: '', lastName: '', email: '', password: '', role: 'viewer', department: '', regionLevel: '', phone: '' }); setShowModal(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          <FiPlus size={18} /> Create User
        </button>
      </div>

      {/* Stats */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Users', value: stats?.total ?? totalUsers, color: '#3b82f6', icon: FiUsers },
          { label: 'Active', value: stats?.active || 0, color: '#10b981', icon: FiCheck },
          { label: 'Pending Approval', value: pending.length, color: '#f59e0b', icon: FiClock },
          { label: 'Admins', value: stats?.admins || 0, color: '#8b5cf6', icon: FiShield },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <s.icon size={20} color={s.color} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{s.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Pending Approvals */}
      {pending.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 16, padding: 20, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#92400e', marginBottom: 16 }}>⏳ Pending Approvals ({pending.length})</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {pending.map(u => (
              <div key={u._id} className="pending-item" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: '#fff', borderRadius: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#f59e0b20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#92400e', fontSize: 14 }}>
                  {u.firstName?.[0]}{u.lastName?.[0]}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{u.firstName} {u.lastName}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{u.email} — {u.department || 'No department'}</div>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(u.createdAt).toLocaleDateString()}</div>
                <div className="pending-actions" style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => handleApprove(u._id)} title="Approve" style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FiCheck size={14} /> Approve
                  </button>
                  <button onClick={() => handleReject(u._id)} title="Reject" style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FiXCircle size={14} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search, filters & Users Table */}
      <div className="filter-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 400 }}>
          <FiSearch size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by name or email..."
            style={{ width: '100%', padding: '10px 14px 10px 42px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <select aria-label="Role" value={filters.role} onChange={e => setFilter('role', e.target.value)} style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#0f172a', cursor: 'pointer', outline: 'none' }}>
          <option value="">All roles</option>
          {Object.entries(allRoleLabels).filter(([k]) => isSuperAdmin || k !== 'super_admin').map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select aria-label="Status" value={filters.status} onChange={e => setFilter('status', e.target.value)} style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#0f172a', cursor: 'pointer', outline: 'none' }}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending approval</option>
          <option value="unverified">Unverified (no code entered)</option>
        </select>
        {isSuperAdmin && (
          <select aria-label="Workspace" value={filters.workspace} onChange={e => setFilter('workspace', e.target.value)} style={{ ...{ padding: '9px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#0f172a', cursor: 'pointer', outline: 'none' }, maxWidth: 220 }}>
            <option value="">All workspaces</option>
            <option value="none">No workspace (free users)</option>
            {workspaces.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
          </select>
        )}
        <select aria-label="Last login" value={filters.lastLogin} onChange={e => setFilter('lastLogin', e.target.value)} style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#0f172a', cursor: 'pointer', outline: 'none' }}>
          <option value="">Any login activity</option>
          <option value="30d">Logged in last 30 days</option>
          <option value="inactive90">No login in 90 days</option>
          <option value="never">Never logged in</option>
        </select>
        <select aria-label="Sort" value={filters.sort} onChange={e => setFilter('sort', e.target.value)} style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', color: '#0f172a', cursor: 'pointer', outline: 'none' }}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name A–Z</option>
          <option value="lastLogin">Recent login</option>
        </select>
        {(activeFilterCount > 0 || search) && (
          <button onClick={() => { setFilters(EMPTY_FILTERS); setSearch(''); setPage(1); }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '9px 12px', borderRadius: 10, border: 'none', background: '#f1f5f9', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <FiX size={14} /> Clear
          </button>
        )}
        <span style={{ fontSize: 13, color: '#64748b', marginLeft: 'auto' }}>{totalUsers} {totalUsers === 1 ? 'user' : 'users'}{activeFilterCount || search ? ' match' : ''}</span>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['User', 'Email', 'Role', 'Plan', 'Workspace', 'Status', 'Last Login', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: roleColors[u.role] + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: roleColors[u.role] }}>
                        {u.firstName?.[0]}{u.lastName?.[0]}
                      </div>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{u.firstName} {u.lastName}</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{u.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: roleColors[u.role] + '15', color: roleColors[u.role] }}>
                      {allRoleLabels[u.role]}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {u.role === 'super_admin' ? (
                      // Super admin access comes from the role, not a plan: they manage the whole platform
                      <span title="Platform owner: full access through the super admin role, independent of any plan" style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#b91c1c', background: '#fef2f2' }}>
                        Platform owner
                      </span>
                    ) : currentUser?.role === 'super_admin' ? (
                      assigningPlan === u._id ? (
                        <span style={{ fontSize: 12, color: '#64748b' }}>Saving...</span>
                      ) : (
                        <div title="Assigns a personal workspace plan for this user. Will not affect other users." style={{ position: 'relative', display: 'inline-block' }}>
                          <select
                            value={u.workspace?.subscription?.plan || 'free'}
                            onChange={e => handleAssignPlan(u._id, e.target.value)}
                            style={{ padding: '4px 24px 4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontWeight: 600, cursor: 'pointer', appearance: 'none', background: '#f8fafc', outline: 'none' }}
                          >
                            {PLAN_OPTIONS.map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                          </select>
                          <FiCreditCard size={11} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                        </div>
                      )
                    ) : (
                      <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#3b82f6', background: '#eff6ff' }}>
                        {(u.workspace?.subscription?.plan || 'free').charAt(0).toUpperCase() + (u.workspace?.subscription?.plan || 'free').slice(1)}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}>{u.workspace?.name || '—'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      background: u.isActive && u.isApproved ? '#dcfce7' : !u.isEmailVerified ? '#ffedd5' : !u.isApproved ? '#fef3c7' : '#fee2e2',
                      color: u.isActive && u.isApproved ? '#166534' : !u.isEmailVerified ? '#9a3412' : !u.isApproved ? '#854d0e' : '#991b1b' }}>
                      {u.isActive && u.isApproved ? 'Active' : !u.isEmailVerified ? 'Unverified' : !u.isApproved ? 'Pending' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8' }}>
                    {u.lastLogin ? new Date(u.lastLogin).toLocaleString() : 'Never'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!u.isEmailVerified && u.isActive && (
                        <button onClick={() => handleResendCode(u)} title="Resend verification code" style={{ background: '#fff7ed', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#ea580c' }}>
                          <FiMail size={14} />
                        </button>
                      )}
                      {!u.isApproved && u.isActive && (
                        <button onClick={() => { if (u.isEmailVerified || window.confirm(`${u.email} hasn't verified their email. Approve anyway? Only do this if you know this person.`)) handleApprove(u._id); }}
                          title={u.isEmailVerified ? 'Approve' : 'Approve without email verification'} style={{ background: '#f0fdf4', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#16a34a' }}>
                          <FiCheck size={14} />
                        </button>
                      )}
                      <button onClick={() => openEdit(u)} style={{ background: '#eff6ff', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#3b82f6' }}>
                        <FiEdit2 size={14} />
                      </button>
                      <button onClick={() => handleDelete(u._id)} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#ef4444' }}>
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No users found</td></tr>}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #f1f5f9', fontSize: 13, color: '#64748b' }}>
            <span>Page {page} of {pages} · {totalUsers} users</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>Previous</button>
              <button disabled={page >= pages} onClick={() => setPage(page + 1)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: page >= pages ? 'not-allowed' : 'pointer', opacity: page >= pages ? 0.5 : 1 }}>Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>{editUser ? 'Edit User' : 'Create User'}</h2>
              <button onClick={() => { setShowModal(false); setEditUser(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div><label style={labelStyle}>First Name</label><input value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} required style={inputStyle} /></div>
                <div><label style={labelStyle}>Last Name</label><input value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} required style={inputStyle} /></div>
              </div>
              <div style={{ marginBottom: 16 }}><label style={labelStyle}>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required style={inputStyle} /></div>
              {!editUser && <div style={{ marginBottom: 16 }}><label style={labelStyle}>Password</label><input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editUser} minLength={6} style={inputStyle} /></div>}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Role</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={inputStyle}>
                  {Object.entries(roleLabels).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                </select>
              </div>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div><label style={labelStyle}>Department</label><input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Region Level</label>
                  <select value={form.regionLevel} onChange={e => setForm({ ...form, regionLevel: e.target.value })} style={inputStyle}>
                    <option value="">Select</option>
                    {['international', 'national', 'state', 'local'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}><label style={labelStyle}>Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} /></div>
              <button type="submit" style={{ width: '100%', padding: 12, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>
                {editUser ? 'Update User' : 'Create User'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };

export default UserManagement;
